import {
    OrderFilter,
    OrderSearchParamsDTO,
    OrderSortDirection,
    OrderSortField,
    OrderTableViewStateDTO
} from "@/api/data-contracts";
import {OverviewState} from "@/common/overview-state/overview-state-model";
import {FilterFieldGroup} from "@/common/overview-state/filter-fields";
import {ORDER_TRACKING_COLUMN_IDS} from "@/common/overview-table/overview-table-column-ids";

/**
 * The opaque filter object `useOverviewTableState` carries for every order-search variant
 * (`OrderFilter.INTERNAL` / `OrderFilter.EXTERNAL` / `OrderFilter.NEUTRAL`). Structurally the persisted
 * document minus `size` — `size` is a generically known `OverviewState` field, not part of the filter
 * object (see `overview-state-model.ts`). Unlike the request side there is only one status field
 * (`orderStatuses`), and the company filter is single-valued (`companyId`) rather than a list. Its
 * effectiveness is entirely server-controlled (`OrderService#resolveCompanyId`): for the Innendienst it
 * restricts the result in the `INTERNAL`/`EXTERNAL` variants, has no effect at all in `NEUTRAL` (the
 * platform-wide view is never restricted), and is replaced with the caller's own company for every
 * other caller regardless of variant. No UI control sets this field yet — that is #1607's job.
 */
export type OrderOverviewFilters = Omit<OrderTableViewStateDTO, "size">;

/**
 * The order table's business field groups for every order-search variant (badge counting, Phase 1.3):
 * status, planned delivery period, confirmed delivery date, the three column contains filters
 * (Auftragsnummer, Angebotsnummer, Lieferadresse) added in Phase 1.2, and the Info column's
 * `hasUnreadMessages` tri-state added in Phase 4.2. `companyId` is not a Sheet field and gets no group,
 * its own control lives outside the Sheet (see the plan's "Standing constraints"). Every `columnId` here
 * is one of `ORDER_TRACKING_COLUMN_IDS`, an explicit `id` on the matching `OrderTrackingTable.tsx` column
 * definition, shared through `overview-table-column-ids.ts` so the two sides cannot drift apart.
 */
export const ORDER_FILTER_FIELD_GROUPS: ReadonlyArray<FilterFieldGroup<OrderOverviewFilters>> = [
    {kind: "statusSelection", fields: ["orderStatuses"], columnId: ORDER_TRACKING_COLUMN_IDS.status},
    {
        kind: "range",
        fields: ["plannedDeliveryFrom", "plannedDeliveryTo"],
        columnId: ORDER_TRACKING_COLUMN_IDS.plannedDeliveryDates
    },
    {
        kind: "range",
        fields: ["confirmedDeliveryDateFrom", "confirmedDeliveryDateTo"],
        columnId: ORDER_TRACKING_COLUMN_IDS.deliveryDate
    },
    {kind: "text", fields: ["orderNumberContains"], columnId: ORDER_TRACKING_COLUMN_IDS.orderNumber},
    {kind: "text", fields: ["quoteNumberContains"], columnId: ORDER_TRACKING_COLUMN_IDS.quoteNumber},
    {kind: "text", fields: ["deliveryAddressContains"], columnId: ORDER_TRACKING_COLUMN_IDS.deliveryAddress},
    {kind: "triState", fields: ["hasUnreadMessages"], columnId: ORDER_TRACKING_COLUMN_IDS.info}
];

/**
 * Maps the persisted document onto the opaque filter object. A missing document (`undefined`, the
 * 204-empty-body case — see `overview-state-model.ts`) yields `defaults` unchanged. In a present document,
 * a missing `orderStatuses` means "never touched" and falls back to `defaults`; an empty list means
 * "nothing selected" and is kept as an empty list — `??` only substitutes on `null`/`undefined`, never on
 * an empty array, which is exactly this distinction. There is no variant branching here (unlike the
 * request side): every order variant shares the same single status field.
 *
 * A stored `hasUnreadMessages: false` is a legacy value from the retired three-way selection (Alle/Ja/Nein):
 * the control now only ever writes `true` or leaves the field untouched, so a persisted `false` is normalized
 * to `undefined` here, hydrating to "Alle" instead of restricting the result list against a control that can
 * no longer express it.
 */
export function orderDocumentToFilters(
    doc: OrderTableViewStateDTO | undefined,
    defaults: OrderOverviewFilters
): OrderOverviewFilters {
    if (doc === undefined) {
        return defaults;
    }
    const {size: _size, orderStatuses: _orderStatuses, ...rest} = doc;
    return {
        ...rest,
        hasUnreadMessages: rest.hasUnreadMessages === false ? undefined : rest.hasUnreadMessages,
        orderStatuses: doc.orderStatuses ?? defaults.orderStatuses
    };
}

/**
 * Maps state onto the document that gets persisted (`PUT /api/table-view-states/orders/{view}`).
 * `searchText`, `page`, `sortField` and `sortDirection` are never part of `OrderOverviewFilters` in the
 * first place (Phase 0, Entscheidung 1 for sorting; the plan's "Standing constraints" for search text and
 * page), so this mapping cannot accidentally leak them — it only adds back `size`, which the document
 * carries but `OverviewState` keeps as its own generic field rather than inside `filters`.
 */
export function orderStateToDocument(
    state: OverviewState<OrderOverviewFilters, OrderSortField>
): OrderTableViewStateDTO {
    return {...state.filters, size: state.size};
}

/**
 * Maps state onto the parameters sent to `POST /api/orders/search` for one order-search variant: the
 * persisted-relevant part (`filters`, `size`) plus the volatile part (`searchText`, `page`, `sortField`,
 * `sortDirection`) that is never written to the document. An empty free-text search is sent as
 * `undefined`, not `""` — the first query after loading an untouched document must show
 * `searchText: undefined`, not an empty string (see the plan's DoD-to-test mapping table).
 */
export function orderStateToSearchParams(
    state: OverviewState<OrderOverviewFilters, OrderSortField>,
    variant: OrderFilter
): OrderSearchParamsDTO {
    return {
        ...state.filters,
        type: variant,
        page: state.page,
        size: state.size,
        searchText: state.searchText === "" ? undefined : state.searchText,
        sortField: state.sortField,
        sortDirection: state.sortDirection as OrderSortDirection | undefined
    };
}
